import React from 'react';
import { useTranslation } from 'react-i18next';
import { Header, ActionButton } from 'semantic';
import {
  Tariff as TariffType,
  TariffDescription,
} from 'screens/GroupsAndPolicies/types';
import { FieldArray, FormikState } from 'formik';
// TODO: ensure styling is only applied to this component
// import './style.less';
import { buildFormPath } from 'screens/GroupsAndPolicies/formpath';
import { FormController } from '../FormController';
import { TariffDescriptionFormFields } from './TariffDescriptionFormFields';
import { TariffDescriptionPreview } from './TariffDescriptionPreview';
import { TariffComponentFormFields } from './TariffComponentFormFields';
import { TariffComponentPreview } from './TariffComponentPreview';
import { descriptionSchema, tariffComponentSchema } from '../validation';
import { Segment } from 'semantic';
import { supportedLanguages } from 'i18n';
import { Container } from 'semantic-ui-react';

type Props = {
  basePath?: string;
  values: FormikState<TariffType>['values'];
  setFieldValue: (field: string, value: any) => void;
};

export const TariffFormFields = (props: Props) => {
  const { t } = useTranslation();

  const getAvailableLanguages = (
    currentLanguage: string,
    descriptions?: TariffDescription[]
  ) =>
    supportedLanguages.filter(
      (l) =>
        l.value === currentLanguage ||
        !descriptions?.find((d) => d.language === l.value)
    );

  return (
    <>
      <Segment>
        <FieldArray name={buildFormPath('components', props.basePath)}>
          {({ push, remove }) => (
            <>
              {props.values?.components?.length > 0 && (
                <Header
                  as="h4"
                  style={{ marginTop: '0' }}
                  content={t('tariff.components', 'Components')}
                />
              )}
              {props.values?.components?.map((component, index) => (
                <FormController
                  key={index}
                  values={component}
                  onSubmit={(values) =>
                    props.setFieldValue(
                      buildFormPath(`components[${index}]`, props.basePath),
                      { ...values, __initial: undefined }
                    )
                  }
                  onRemove={() => remove(index)}
                  validationSchema={tariffComponentSchema}
                  formComponent={TariffComponentFormFields}
                  previewComponent={TariffComponentPreview}
                  openInEditMode={component.__initial}
                  removeOnCancel={component.__initial}
                />
              ))}

              <div>
                <ActionButton
                  primary
                  compact
                  icon="plus"
                  content={t(
                    'tariff.addTariffComponent',
                    'Add tariff component'
                  )}
                  onClick={() => push({ __initial: true })}
                />
              </div>
            </>
          )}
        </FieldArray>
      </Segment>
      <Segment>
        <FieldArray name={buildFormPath('descriptions', props.basePath)}>
          {({ push, remove }) => (
            <>
              <div>
                {!props.values?.descriptions?.length && (
                  <>
                    <ActionButton
                      primary
                      compact
                      icon="plus"
                      content={t(
                        'tariff.addCustomDescription',
                        'Add custom description'
                      )}
                      onClick={() => push({ __initial: true })}
                    />
                  </>
                )}
              </div>

              {(props.values?.descriptions || []).length > 0 && (
                <>
                  <Header as={'h4'} style={{ marginTop: '6px' }}>
                    {t(
                      'tariffProfile.customTariffDescriptionsTitle',
                      'Custom tariff descriptions'
                    )}
                  </Header>
                  {props.values?.descriptions?.map((description, i) => (
                    <FormController
                      key={description.__id || i}
                      values={description}
                      onSubmit={(values) =>
                        props.setFieldValue(
                          buildFormPath(`descriptions[${i}]`, props.basePath),
                          { ...values, __initial: undefined }
                        )
                      }
                      onRemove={() => remove(i)} // Map onReset to onRemove
                      validationSchema={descriptionSchema}
                      formComponent={(p: any) => (
                        <TariffDescriptionFormFields
                          {...p}
                          getAvailableLanguages={(currentLanguage: string) =>
                            getAvailableLanguages(
                              currentLanguage,
                              props.values?.descriptions
                            )
                          }
                        />
                      )}
                      previewComponent={TariffDescriptionPreview}
                      openInEditMode={description.__initial}
                      removeOnCancel={description.__initial}
                    />
                  ))}

                  <Container>
                    <ActionButton
                      primary
                      compact
                      icon="plus"
                      content={t('tariffProfile.addLanguage', 'Add language')}
                      onClick={() => push({ __initial: true })}
                    />
                  </Container>
                </>
              )}
            </>
          )}
        </FieldArray>
      </Segment>
    </>
  );
};
